// ── drop-killer — Entry point ─────────────────────
// Orden del boot, y por qué:
//   1. tema      — hereda el data-theme que puso el shell y ata el botón
//   2. pantalla  — viewport / touch / reduced en <html>
//   3. lenis     — el scroll suave existe antes que cualquier vista
//   4. marco     — el fondo, el rail, el bisel y la barra de herramientas
//                  (👨NAV); viven en todas las páginas, la vista sólo
//                  rellena el panel y los huecos de la barra
//   5. ayuda     — la barra de ayuda (💬HLP) escucha en document
//   6. fondo     — las manchas siguen al ratón (🌌BGG, sólo ratón fino)
//   7. loader    — corre en paralelo con la primera vista y cierra el telón
//                  convirtiéndolo en el rail (⏳LDR)
//
// Después del boot, las vistas se cambian AQUÍ, sin recargar: el router
// escucha hashchange (#/settings… → ⚙️SET, el resto → 📂BRW) y hace la
// transición —la vista que se va baja la opacidad y sube 8px en .14s, se
// mata, se monta la nueva, y entra desde 8px abajo en .18s— con cola de uno:
// si llega otro cambio a mitad, se queda con el último.

import '@style/🖌️STY.pcss'

import { animate, swiftOut }            from '@anm/🪇ANM.js'
import { initSCR }                      from '@scr/📺SCR.js'
import { initSLL }                      from '@sll/🪀SLL.js'
import { CAL, APIError }                from '@js/☎️CAL.js'
import { bus }                          from '@js/🚌BUS.js'
import { setPage }                      from '@js/⚙️OPS.js'
import { initTheme }                    from '@comps/🌗THM/🌗THM.js'
import { initNav, setNavPage }          from '@comps/👨NAV/👨NAV.js'
import { initHelp, hideHelp }           from '@comps/💬HLP/💬HLP.js'
import { initBGG }                      from '@js/🌌BGG.js'
import { mountLDR, initLDR, endLDR }    from '@comps/⏳LDR/⏳LDR.js'

// ── Booting ─────────────────────────────────────
console.log('[drop-killer] booting')

initTheme()
initSCR()
initSLL()
initNav()
initHelp()
initBGG()
mountLDR()

// ── Estado del router ───────────────────────────
const cfg = window.__DROP__ || {}

let me   = null     // la sesión (/api/me), una vez verificada
let page = null     // 'login' | 'browser' | 'settings'
let view = null     // la instancia montada (con kill())
let swapping = false
let queued = null   // cola de uno: el último cambio pedido durante un swap

const OUT = { opacity: [1, 0], y: [0, -8], duration: .14, ease: swiftOut }
const IN  = { opacity: [0, 1], y: [8, 0],  duration: .18, ease: swiftOut }

// Qué página pide el hash. Login sólo si no hay sesión (o es el bootstrap).
function pageFor(hash) {
  if (cfg.bootstrap_token) return 'login'
  if (!me) return 'login'
  return (hash || '').startsWith('#/settings') ? 'settings' : 'browser'
}

// Monta la vista de una página. Devuelve la instancia (o null).
async function mount(next) {
  setPage(next)
  setNavPage(next)
  if (next === 'login') {
    const mod = await import('@viu/🔐LGN/🔐LGN.js')
    return mod.initLGN({ bootstrap: !!cfg.bootstrap_token, token: cfg.bootstrap_token || null })
  }
  if (next === 'settings') {
    const mod = await import('@viu/⚙️SET/⚙️SET.js')
    return mod.initSET({ me })
  }
  const mod = await import('@viu/📂BRW/📂BRW.js')
  return mod.initBRW({ me })
}

// anime deja transform y opacity inline; en .MAIN un transform que se queda
// es el bloque contenedor de todo position:fixed que haya dentro.
const limpia = (el) => { if (el) { el.style.transform = ''; el.style.opacity = ''; el.style.willChange = '' } }

// La transición entre páginas. Cola de uno.
async function swap(next) {
  if (swapping) { queued = next; return }
  if (next === page) return
  swapping = true
  hideHelp()

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const out = document.getElementById('main')

  try {
    if (out && !reduce) {
      await new Promise(resolve => animate(out, { ...OUT, onComplete: resolve }))
    }
    view?.kill?.()
    view = null
    window.scrollTo(0, 0)
    view = await mount(next)
    page = next

    const inn = document.getElementById('main')
    if (inn && !reduce) {
      await new Promise(resolve => animate(inn, { ...IN, onComplete: () => { limpia(inn); resolve() } }))
    }
  } catch (err) {
    if (err instanceof APIError && err.status === 401) {
      // La sesión caducó a mitad: al login
      me = null
      view?.kill?.()
      view = await mount('login')
      page = 'login'
      limpia(document.getElementById('main'))
    } else {
      console.error('[drop-killer] swap failed', err)
    }
  } finally {
    swapping = false
    if (queued) { const q = queued; queued = null; swap(q) }
  }
}

// ── La primera vista (sin transición: el telón la revela) ──
// Resuelve SIEMPRE: el loader espera a esto para cerrarse.
async function mountFirst() {
  if (!cfg.bootstrap_token) {
    try {
      me = await CAL.me()
    } catch (err) {
      if (!(err instanceof APIError && err.status === 401)) {
        console.error('[drop-killer] /api/me failed', err)
      }
      me = null
    }
  }
  try {
    page = pageFor(window.location.hash)
    view = await mount(page)
  } catch (err) {
    console.error('[drop-killer] boot failed', err)
  }
}

// ── El router ───────────────────────────────────
window.addEventListener('hashchange', () => {
  const next = pageFor(window.location.hash)
  if (next !== page) swap(next)
  // Si es la misma página, es un cambio de carpeta: lo atiende 📂BRW (popstate).
})

// El rail: archivos / ajustes. Si ya estás en el browser, «archivos» vuelve
// a la carpeta privada sin cambiar de página.
bus.on('nav:section', ({ key }) => {
  if (key === 'settings') {
    if (page !== 'settings') window.location.hash = '#/settings/devices'
    return
  }
  if (key === 'files') {
    if (page === 'browser') bus.emit('nav:goto', { path: '/private' })
    else window.location.hash = '#/private'
  }
})

// El login: con la sesión puesta, al browser sin recargar.
bus.on('auth:ok', async () => {
  try { me = await CAL.me() } catch { me = null }
  if (!me) { window.location.reload(); return }
  cfg.bootstrap_token = null
  if (!window.location.hash || window.location.hash.startsWith('#/settings')) {
    window.history.replaceState({}, '', '#/private')
  }
  swap(pageFor(window.location.hash))
})

// ── Boot ────────────────────────────────────────
async function boot() {
  const [ANMcounter] = await Promise.all([initLDR(), mountFirst()])
  await endLDR(ANMcounter)
  console.log('[drop-killer] front vivo')
}

boot()
